import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardCheck, Loader2, Plus, Trash2, Save, Gauge, Search, Home, FileText, Settings2, X, ShieldAlert } from "lucide-react";
import { api, adminHeaders } from "@/lib/api";
import { toast } from "sonner";
import PropertyDetailMap, { ATTENTION, ATTENTION_ORDER } from "@/components/PropertyDetailMap";
import { SCORE_LABELS } from "@/components/PropertyScoreCard";

const h = () => ({ headers: adminHeaders() });
const inputCls = "w-full rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-gold/60";
const ZONE_TYPES = ["kitchen", "bathroom", "bedroom", "living", "balcony", "windows", "storage", "other"];
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
const scoreColor = (t) => (t >= 75 ? "#f4a4a4" : t >= 50 ? "#E7CE93" : "#6ee7b7");

export default function AssessmentWorkbench({ onOpenQuote }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);       // selected property (full)
  const [zones, setZones] = useState([]);
  const [scoreInputs, setScoreInputs] = useState({});
  const [rec, setRec] = useState("");
  const [saving, setSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const loadList = useCallback(() => {
    api.get("/admin/client-properties", h()).then(({ data }) => setList(data || [])).catch(() => setList([]));
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const open = async (pid) => {
    try {
      const { data } = await api.get(`/admin/client-properties/${pid}`, h());
      setSel(data);
      setZones(data.zones || []);
      setScoreInputs((data.score && data.score.inputs) || {});
      setRec(data.recommendation || "");
    } catch { toast.error("Could not open property"); }
  };

  const setZone = (i, k, v) => setZones((z) => { const a = [...z]; a[i] = { ...a[i], [k]: v }; return a; });
  const addZone = () => setZones((z) => [...z, { id: uid(), name: "", type: "other", attention: "standard", scope: "", notes: "", detail_points: [] }]);
  const rmZone = (i) => setZones((z) => z.filter((_, x) => x !== i));

  const save = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/client-properties/${sel.id}`,
        { zones, score_inputs: scoreInputs, recommendation: rec }, h());
      setSel(data); setZones(data.zones || []); setScoreInputs((data.score && data.score.inputs) || {}); setRec(data.recommendation || "");
      toast.success(`Assessment saved · Score ${data.score?.total ?? "—"}/100`);
      loadList();
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const generateQuote = async () => {
    if (!sel) return;
    setGenLoading(true);
    try {
      // Persist the latest assessment first so the quote carries fresh data.
      await save();
      const { data } = await api.post(`/admin/client-properties/${sel.id}/generate-quote`, {}, h());
      toast.success(`Visual quote ready · ${data.ref}`);
      if (onOpenQuote) onOpenQuote(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate quote"); }
    finally { setGenLoading(false); }
  };

  const shown = (list || []).filter((p) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [p.nickname, p.client_name, p.city, p.address].some((v) => String(v || "").toLowerCase().includes(s));
  });
  const liveTotal = sel?.score?.total;

  return (
    <div className="space-y-5" data-testid="assessment-workbench">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="kicker mb-1">Property OS</p>
          <h2 className="font-display text-2xl font-semibold text-white flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-gold-soft" /> Assessment Workbench</h2>
          <p className="text-sm text-muted-c mt-1">Review a client property, set zone attention and complexity scores. This shapes the client's Detail Map, Property Score and quote recommendation.</p>
        </div>
        <button onClick={() => setShowSettings(true)} data-testid="wb-open-settings"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 text-xs text-white/80 hover:bg-white/5 hover:text-white">
          <Settings2 className="h-3.5 w-3.5 text-gold-soft" /> Settings
        </button>
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        {/* LEFT: property list */}
        <div className="lg:col-span-4">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-c" />
            <input className={inputCls + " pl-9"} placeholder="Search properties / clients…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wb-search" />
          </div>
          {list === null ? <p className="text-sm text-muted-c">Loading…</p> : shown.length === 0 ? (
            <div className="ascendia-card p-6 text-center text-sm text-muted-c">No client properties yet.</div>
          ) : (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {shown.map((p) => (
                <button key={p.id} onClick={() => open(p.id)} data-testid={`wb-prop-${p.id}`}
                  className={`w-full text-left rounded-xl border p-3.5 transition-colors ${sel?.id === p.id ? "bg-white/[0.06] border-gold/40" : "border-[color:var(--border-subtle)] hover:bg-white/[0.03]"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white font-medium text-sm truncate flex items-center gap-1.5"><Home className="h-3.5 w-3.5 text-gold-soft" /> {p.nickname}</span>
                    {typeof p.score?.total === "number" && <span className="text-[11px] px-2 py-0.5 rounded-full border" style={{ color: scoreColor(p.score.total), borderColor: "var(--border-subtle)" }}>{p.score.total}</span>}
                  </div>
                  <p className="text-xs text-muted-c mt-1 truncate">{[p.client_name, p.city].filter(Boolean).join(" · ") || "—"}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: assessment editor */}
        <div className="lg:col-span-8">
          {!sel ? (
            <div className="ascendia-card p-10 text-center text-sm text-muted-c">Select a property to begin the assessment.</div>
          ) : (
            <div className="space-y-5">
              <div className="ascendia-card p-5">
                <h3 className="font-display text-lg font-semibold text-white">{sel.nickname}</h3>
                <p className="text-sm text-muted-c mt-0.5">{[sel.property_type, sel.address || sel.city, sel.sqm && `${sel.sqm} sqm`].filter(Boolean).join(" · ")}</p>
                {(sel.problem_areas || []).length > 0 && <p className="text-xs text-secondary mt-2">Flagged: {sel.problem_areas.join(", ")}</p>}
              </div>

              {/* Zones attention */}
              <div className="ascendia-card p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium">Zone attention</h4>
                  <button onClick={addZone} className="inline-flex items-center gap-1 text-xs text-gold-soft hover:text-gold" data-testid="wb-add-zone"><Plus className="h-3.5 w-3.5" /> Add zone</button>
                </div>
                {zones.length > 0 && <div className="mb-4"><PropertyDetailMap zones={zones} /></div>}
                <div className="space-y-2">
                  {zones.map((z, i) => (
                    <div key={z.id || i} className="grid grid-cols-12 gap-2 items-center">
                      <input className={inputCls + " col-span-3"} placeholder="Zone" value={z.name} onChange={(e) => setZone(i, "name", e.target.value)} data-testid={`wb-zone-name-${i}`} />
                      <select className={inputCls + " col-span-2"} value={z.type || "other"} onChange={(e) => setZone(i, "type", e.target.value)}>{ZONE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select>
                      <select className={inputCls + " col-span-3"} value={z.attention || "standard"} onChange={(e) => setZone(i, "attention", e.target.value)} data-testid={`wb-zone-att-${i}`}>{ATTENTION_ORDER.map((k) => <option key={k} value={k}>{ATTENTION[k].label}</option>)}</select>
                      <input className={inputCls + " col-span-3"} placeholder="Scope" value={z.scope || ""} onChange={(e) => setZone(i, "scope", e.target.value)} />
                      <button onClick={() => rmZone(i)} className="col-span-1 flex justify-center text-muted-c hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  {!zones.length && <p className="text-xs text-muted-c">No zones yet — add the rooms you assessed.</p>}
                </div>
              </div>

              {/* Property Score sliders */}
              <div className="ascendia-card p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium flex items-center gap-2"><Gauge className="h-4 w-4 text-gold-soft" /> Complexity score</h4>
                  {typeof liveTotal === "number" && <span className="text-sm font-semibold" style={{ color: scoreColor(liveTotal) }} data-testid="wb-score-total">{liveTotal}/100</span>}
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {Object.keys(SCORE_LABELS).map((k) => (
                    <div key={k} className="flex items-center gap-3">
                      <span className="w-28 shrink-0 text-[11px] text-muted-c">{SCORE_LABELS[k]}</span>
                      <input type="range" min="0" max="10" step="1" value={scoreInputs[k] ?? 0}
                        onChange={(e) => setScoreInputs((s) => ({ ...s, [k]: Number(e.target.value) }))}
                        className="flex-1 accent-[color:var(--gold)]" data-testid={`wb-score-${k}`} />
                      <span className="w-5 text-right text-xs text-secondary">{scoreInputs[k] ?? 0}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-c mt-3">Score is saved and recomputed with admin weights. Internal planning aid — clients see the total + rationale on their passport.</p>
              </div>

              {/* Recommendation */}
              <div className="ascendia-card p-5">
                <h4 className="text-white font-medium mb-2">Service recommendation (shown to client)</h4>
                <textarea rows={2} className={inputCls} value={rec} onChange={(e) => setRec(e.target.value)} placeholder="e.g. Complete Post-Construction Detail recommended, prioritising windows, cabinetry and residue." data-testid="wb-recommendation" />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={save} disabled={saving} data-testid="wb-save" className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border border-[color:var(--border-subtle)] px-6 py-3 text-sm font-semibold text-white hover:bg-white/5 disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save assessment
                </button>
                <button onClick={generateQuote} disabled={genLoading || saving} data-testid="wb-generate-quote" className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-60">
                  {genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generate visual quote
                </button>
              </div>
              <p className="text-[11px] text-muted-c text-center">Generating a quote saves this assessment and carries the Detail Map + recommendation into a premium proposal draft.</p>
            </div>
          )}
        </div>
      </div>

      {showSettings && (
        <PropertyOsSettings onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}


function PropertyOsSettings({ onClose }) {
  const [weights, setWeights] = useState(null);
  const [minMargin, setMinMargin] = useState(40);
  const [travelRate, setTravelRate] = useState(3.5);
  const [baseAddress, setBaseAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/score-weights", h()),
      api.get("/admin/ops-settings", h()),
    ]).then(([w, o]) => {
      setWeights(w.data || {});
      setMinMargin(o.data?.min_margin ?? 40);
      setTravelRate(o.data?.travel_rate_per_km ?? 3.5);
      setBaseAddress(o.data?.base_address ?? "");
    }).catch(() => toast.error("Could not load settings")).finally(() => setLoading(false));
  }, []);

  const setW = (k, v) => setWeights((s) => ({ ...s, [k]: v }));

  const saveAll = async () => {
    setSaving(true);
    try {
      const w = {};
      Object.keys(weights || {}).forEach((k) => { w[k] = Number(weights[k]); });
      await Promise.all([
        api.put("/admin/score-weights", w, h()),
        api.put("/admin/ops-settings", { min_margin: Number(minMargin), travel_rate_per_km: Number(travelRate), base_address: baseAddress }, h()),
      ]);
      toast.success("Property OS settings saved");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="wb-settings-modal">
      <div className="w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl border border-[color:var(--border-subtle)] bg-[#101011] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <div>
            <p className="kicker mb-1">Property OS</p>
            <h3 className="font-display text-xl font-semibold text-white flex items-center gap-2"><Settings2 className="h-4.5 w-4.5 text-gold-soft" /> Settings</h3>
          </div>
          <button onClick={onClose} className="text-muted-c hover:text-white" data-testid="wb-settings-close"><X className="h-5 w-5" /></button>
        </div>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-muted-c"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Score weights */}
            <section>
              <div className="flex items-center gap-2 mb-1"><Gauge className="h-4 w-4 text-gold-soft" /><h4 className="text-white font-medium">Property Score weights</h4></div>
              <p className="text-[11px] text-muted-c mb-3">Tune how much each complexity factor influences the overall property score (0–3). Higher = greater impact.</p>
              <div className="space-y-3">
                {Object.keys(weights || {}).map((k) => (
                  <div key={k} className="flex items-center gap-3">
                    <span className="w-32 shrink-0 text-[11px] text-muted-c">{SCORE_LABELS[k] || k.replace(/_/g, " ")}</span>
                    <input type="range" min="0" max="3" step="0.1" value={weights[k]}
                      onChange={(e) => setW(k, e.target.value)} className="flex-1 accent-[color:var(--gold)]" data-testid={`wb-weight-${k}`} />
                    <span className="w-8 text-right text-xs text-secondary">{Number(weights[k]).toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </section>

            <div className="gold-hairline" />

            {/* Margin guardrail */}
            <section>
              <div className="flex items-center gap-2 mb-1"><ShieldAlert className="h-4 w-4 text-gold-soft" /><h4 className="text-white font-medium">Margin guardrail</h4></div>
              <p className="text-[11px] text-muted-c mb-3">Quotes with a profit margin below this threshold are flagged so no job gets underpriced.</p>
              <div className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-[11px] text-muted-c">Minimum margin %</span>
                <input type="range" min="0" max="80" step="1" value={minMargin}
                  onChange={(e) => setMinMargin(e.target.value)} className="flex-1 accent-[color:var(--gold)]" data-testid="wb-min-margin" />
                <span className="w-10 text-right text-sm font-semibold text-gold-soft">{Number(minMargin).toFixed(0)}%</span>
              </div>
              <p className="text-[11px] text-muted-c mt-4 mb-1">Travel is charged round trip and added to job cost — so far jobs never quietly slip below margin.</p>
              <div className="flex items-center gap-3">
                <span className="w-32 shrink-0 text-[11px] text-muted-c">Travel rate ₪/km</span>
                <input type="range" min="0" max="20" step="0.5" value={travelRate}
                  onChange={(e) => setTravelRate(e.target.value)} className="flex-1 accent-[color:var(--gold)]" data-testid="wb-travel-rate" />
                <span className="w-12 text-right text-sm font-semibold text-gold-soft">₪{Number(travelRate).toFixed(1)}</span>
              </div>
              <div className="mt-4">
                <p className="text-[11px] text-muted-c mb-1">Base address — where distance is measured from for auto-fill (Google Maps).</p>
                <input value={baseAddress} onChange={(e) => setBaseAddress(e.target.value)} data-testid="wb-base-address"
                  placeholder="e.g. 12 Rothschild Blvd, Tel Aviv, Israel"
                  className="w-full rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-gold/60" />
              </div>
            </section>

            <button onClick={saveAll} disabled={saving} data-testid="wb-settings-save"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
